import { useLazyQuery } from "@apollo/client"
import { isEmpty, omit, pick } from "lodash-es"
import dayjs from "dayjs"
import React, { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import CustomButton from "components/Atoms/CustomButton"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import ButtonMutation from "components/Molecules/ButtonMutation"
import { withFormController } from "components/Molecules/FormController"
import GoogleMapDialog from "components/Templates/GoogleMapDialog"
import { useModuleDcs } from "graphql/order/order.hooks"
import {
  ADD_TEMPORARY_ORDER,
  ADD_UPDATE_CONSOLIDATED_ORDER,
} from "graphql/order/order.mutations"
import { CART_INVOICE, ORDER_LIST } from "graphql/order/order.queries.gql"
import { useCurrentDepartment } from "hooks/useAuth"
import { useCurrentDc } from "store/auth.redux"
import AppToaster, { ApolloErrorToast } from "utils/AppToaster"
import { getId } from "utils/custom.utils"
import InvoiceSummary from "../detail/InvoiceSummary"
import useOrderIds from "./useOrderIds"
import { useOrderFilterVariables } from "graphql/dispatch/dispatch.hooks"
import useSearchParams from "hooks/useSearchParams"

const OrderSummary = ({ order, onOrderAdd, defaultAddress }) => {
  const [dc] = useCurrentDc()
  const location = useLocation()
  const departmentId = useCurrentDepartment()
  const orders = useOrderIds()
  const [params] = useSearchParams()
  const total = params.total
  const isConsolidated = orders?.length > 0 || (total && parseInt(total) > -1)
  const dcs = useModuleDcs("orders")
  const [getInvoice, { data }] = useLazyQuery(CART_INVOICE)
  const [address, setAddress] = useState(defaultAddress)
  const navigate = useNavigate()
  const orderFilter = useOrderFilterVariables()

  const parsedVariables = useMemo(() => {
    return {
      id: order?.id,
      userId: order.user?.id,
      orderItems: order.orderItems?.map(o => ({
        id: o?.id,
        sellableItemStockId: o?.sellableItemStock?.id,
        orderingItemStockId: o?.orderingItemStock?.id,
        quantity: o?.quantity,
        rate:
          order?.orderCategory?.id === "T2"
            ? o?.sellableItemStock?.discountedRate
            : isConsolidated
            ? o?.sourceDcRate
            : null,
      })),
      pickupSlots:
        order.shippingMethod === "SPK"
          ? order?.pickupSlots?.map(p => ({
              distributionCentreId: dc?.id,
              timeSlotId: p?.timeSlot?.id,
            }))
          : [],
      shippingMethod: order.shippingMethod,
      billingAddress: omit(address, ["id", "__typename"]),
      orderCategory: order?.orderCategory?.id,
    }
  }, [order, isConsolidated, dc, address])

  const parsedVariablesConsolidated = useMemo(() => {
    return {
      ...pick(parsedVariables, ["id", "userId", "orderItems"]),
      date: dayjs(order?.date).format("YYYY-MM-DD"),
      orderIds: orders,
      orderingDcId: dc?.id,
      placedInId: departmentId,
      targetDcId: order?.targetDcId?.id,
      orderFilter: total && parseInt(total) > 0 ? orderFilter : undefined,
      category:
        orders?.length > 0 || (total && parseInt(total) > 0)
          ? "T6"
          : order?.orderCategory?.id,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedVariables, departmentId, orders, dc, order, orderFilter])

  const orderItems = parsedVariables?.orderItems

  useEffect(() => {
    getInvoice({ variables: { orderItems } })
  }, [orderItems, getInvoice])

  const paymentRoute = `/d/ecom/orders/add/${
    isConsolidated ? "new-consolidated" : "new"
  }/payment`

  return (
    <>
      <InvoiceSummary invoice={data?.customInvoiceDetail}>
        <div>
          <CustomFormGroup className="mt-3" label="Billing Address">
            <GoogleMapDialog
              value={address}
              data-testid="address-button"
              onChange={(_, value) => setAddress(value)}
              btnProps={{
                className: "self-start",
                big: false,
                dark: address?.village ? false : true,
                fill: false,
                intent: address?.village ? "success" : "none",
                text: address?.village || "Select Address",
              }}
            />
          </CustomFormGroup>
          {isEmpty(address) ? null : (
            <div className="mt-2">
              <p className="mb-0 text-sm">
                {address?.village}, {address?.district}
              </p>
              {address?.address && (
                <p className="text-xs">{address?.address}</p>
              )}
            </div>
          )}
        </div>
      </InvoiceSummary>
      <div className="flex justify-end items-baseline">
        {order?.id ? (
          <CustomButton
            icon="saved"
            text="Confirm"
            dark
            onClick={e =>
              navigate({
                pathname: paymentRoute,
                search: location.search,
              })
            }
          />
        ) : (
          <ButtonMutation
            icon="saved"
            text="Confirm"
            data-testid="confirm-button"
            refetchQueries={[
              {
                query: ORDER_LIST,
                variables: {
                  filter: {
                    distributionCentreIds: dcs?.map(getId),
                  },
                  first: 15,
                  skip: 0,
                },
              },
            ]}
            mutation={
              orders?.length > 0 || (total && parseInt(total) > -1)
                ? ADD_UPDATE_CONSOLIDATED_ORDER
                : ADD_TEMPORARY_ORDER
            }
            variables={
              orders?.length > 0 || (total && parseInt(total) > -1)
                ? parsedVariablesConsolidated
                : parsedVariables
            }
            onCompleted={data => {
              const value = data[Object.keys(data)[0]]
              const ord = value?.order
              AppToaster.show({
                message: `${
                  ord?.isConolidated ? "Consolidated Order" : "Order"
                } added successfully!, the tracking code is ${
                  ord?.trackingCode
                }`,
                intent: "success",
                icon: "tick",
                timeout: 6000,
              })
              onOrderAdd(ord)
              navigate({
                pathname: paymentRoute,
                search: location.search,
              })
            }}
            onError={err => {
              ApolloErrorToast(err, message => {
                return message?.includes("shipping_address_id")
                  ? "Please add a valid current address to the user"
                  : message
              })
            }}
          />
        )}
      </div>
    </>
  )
}

export default withFormController(OrderSummary)
